import { useState } from 'react';
import { Gauge, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { usePerformanceMetrics } from '@/hooks/usePerformanceMetrics';
import { toast } from 'sonner';

function fpsColor(fps: number): string {
  if (fps >= 50) return 'text-emerald-500';
  if (fps >= 30) return 'text-amber-500';
  return 'text-destructive';
}

/**
 * Dev-only floating performance panel. Triggered from a TopHeader button
 * and renders nothing in production builds.
 */
export function PerformancePanel() {
  if (!import.meta.env.DEV) return null;
  return <PerformancePanelInner />;
}

function PerformancePanelInner() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const metrics = usePerformanceMetrics(open);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(metrics, null, 2));
      setCopied(true);
      toast.success('Métricas copiadas al portapapeles');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('No se pudo copiar al portapapeles');
    }
  };

  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex items-center justify-between gap-4 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title="Métricas de rendimiento (dev)"
          aria-label="Abrir panel de métricas de rendimiento"
        >
          <Gauge className="h-4 w-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" sideOffset={6}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold flex items-center gap-1.5">
            <Gauge className="h-4 w-4 text-primary" aria-hidden="true" />
            Performance (dev)
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleCopy}
            aria-label="Copiar métricas como JSON"
            title="Copiar JSON"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>

        <div className="border-t border-border pt-2 space-y-0.5">
          <Row
            label="FPS"
            value={<span className={fpsColor(metrics.fps)}>{metrics.fps}</span>}
          />
          <Row
            label="JS Heap"
            value={
              metrics.memoryUsedMB != null
                ? `${metrics.memoryUsedMB} / ${metrics.memoryLimitMB} MB`
                : 'n/d'
            }
          />
          <Row
            label="TTFB"
            value={metrics.ttfbMs != null ? `${metrics.ttfbMs} ms` : 'n/d'}
          />
          <Row
            label="DOMContentLoaded"
            value={
              metrics.domContentLoadedMs != null
                ? `${metrics.domContentLoadedMs} ms`
                : 'n/d'
            }
          />
          <Row
            label="Load"
            value={
              metrics.navigationLoadMs != null
                ? `${metrics.navigationLoadMs} ms`
                : 'n/d'
            }
          />
          <Row label="Renders (panel)" value={metrics.renderCount} />
          <Row
            label="Ruta"
            value={<span className="truncate max-w-[150px]">{metrics.url}</span>}
          />
        </div>

        <p className="mt-2 text-[10px] text-muted-foreground leading-snug">
          Solo visible en desarrollo. Las métricas se actualizan cada segundo
          mientras el panel está abierto.
        </p>
      </PopoverContent>
    </Popover>
  );
}
